import { cn } from '@/lib/utils';
import { ReactNode, useEffect, useState } from 'react';
import Header from './Header';
import Sidebar from './Sidebar';

interface LayoutProps {
  children: ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Initialize sidebar state from localStorage
  useEffect(() => {
    const savedState = localStorage.getItem('sidebar-collapsed');
    if (savedState !== null && window.innerWidth >= 1024) {
      setIsSidebarCollapsed(JSON.parse(savedState));
    }
  }, []);

  const handleMobileMenuClick = () => {
    setIsMobileSidebarOpen(true);
  };

  // Close mobile sidebar when clicking outside or on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMobileSidebarOpen) {
        setIsMobileSidebarOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobileSidebarOpen]);

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar - Full height from top */}
      <Sidebar 
        isCollapsed={isSidebarCollapsed}
        setIsCollapsed={setIsSidebarCollapsed}
        isMobileOpen={isMobileSidebarOpen}
        setIsMobileOpen={setIsMobileSidebarOpen}
      />
      
      {/* Main content area - Header + Content */}
      <div className={cn(
        "flex-1 flex flex-col transition-all duration-300 ease-in-out min-h-screen",
        // Adjust margin based on sidebar state (desktop only)
        isSidebarCollapsed ? "lg:ml-16" : "lg:ml-64"
      )}>
        <Header onMenuClick={handleMobileMenuClick} />
        <main className="flex-1 overflow-auto">
          <div className="p-4 sm:p-6 max-w-none mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;